// The GUI languages the catalog ships (see doka-pih: "GUI-Sprachen Start: Deutsch + Englisch") and
// the small amount of logic around picking one. Kept separate from labels.ts (which holds the
// strings) and state.ts (which holds the atom) so branding/auth code can normalise a locale without
// pulling in either.

export const SUPPORTED_LOCALES = ['de', 'en'] as const;

export type CatalogLocale = (typeof SUPPORTED_LOCALES)[number];

/** Language select options - the language's own name, as language pickers conventionally do. */
export const LOCALE_OPTIONS: { value: CatalogLocale; label: string }[] = [
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'English' }
];

export const DEFAULT_LOCALE: CatalogLocale = 'de';

/** localStorage key behind catalogLocaleAtom - the user's chosen language survives a reload. */
export const LOCALE_STORAGE_KEY = 'pih.locale';

// Separate marker for "the user picked this themselves", written by the language select only.
// LOCALE_STORAGE_KEY alone can't answer that: UserLocaleSync writing the token language to the atom
// persists it too, and the very next visit would then look like an explicit choice and lock the
// token language out forever.
const LOCALE_CHOSEN_KEY = 'pih.locale.chosen';

/**
 * Maps anything language-tag-shaped onto a supported locale: "de-AT", "DE", "de-AT de" (an OIDC
 * `ui_locales` claim is a space-separated list) all become "de". Undefined when nothing matches -
 * callers decide whether to fall back.
 */
export const normalizeLocale = (raw: string | null | undefined): CatalogLocale | undefined => {
    if (!raw) {
        return undefined;
    }
    for (const tag of raw.trim().split(/[\s,]+/)) {
        const primary = tag.toLowerCase().split(/[-_]/)[0];
        const match = SUPPORTED_LOCALES.find((locale) => locale === primary);
        if (match) {
            return match;
        }
    }
    return undefined;
};

/** Whether the user has ever actively picked a language in this browser. */
export const hasUserChosenLocale = (): boolean =>
    typeof window !== 'undefined' && window.localStorage.getItem(LOCALE_CHOSEN_KEY) === 'true';

/** Called by the language select when the USER switches language - see hasUserChosenLocale. */
export const markLocaleChosen = () => {
    if (typeof window !== 'undefined') {
        window.localStorage.setItem(LOCALE_CHOSEN_KEY, 'true');
    }
};

/**
 * Startup language when the user hasn't picked one yet: the browser's own preference, else German.
 * A logged-in user's language from the token wins over this - but only once the token is there, so
 * it can't be part of a synchronous module-load default (see UserLocaleSync).
 */
export const initialLocale = (): CatalogLocale => {
    if (typeof navigator === 'undefined') {
        return DEFAULT_LOCALE;
    }
    return normalizeLocale(navigator.languages?.join(' ') ?? navigator.language) ?? DEFAULT_LOCALE;
};
